import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { CheckCircle2, Eye, EyeOff } from "lucide-react";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";

export default function Wholesale() {
  const { user, register } = useAuth();
  const [form, setForm] = useState({
    name: "", email: "", password: "", phone: "",
    business_name: "", business_type: "", island: "", position: "",
  });
  const [busy, setBusy] = useState(false);
  const [showPw, setShowPw] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [products, setProducts] = useState([]);

  const approved = user?.role === "wholesale" && user?.status === "approved";
  const pending = user?.role === "wholesale" && user?.status === "pending";

  useEffect(() => {
    if (approved || user?.role === "admin") {
      api.get("/wholesale/products").then(r => setProducts(r.data)).catch(() => {});
    }
  }, [approved, user]);

  const onSubmit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await register({ ...form, role: "wholesale" });
      setSubmitted(true);
      toast.success("Application received. We'll be in touch within 2–3 business days.");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not submit application.");
    } finally { setBusy(false); }
  };

  return (
    <div data-testid="wholesale-page" className="fade-up">
      <section className="max-w-[1440px] mx-auto px-6 md:px-12 pt-24 md:pt-32 pb-16">
        <div className="grid md:grid-cols-12 gap-10 items-start">
          <div className="md:col-span-7">
            <p className="overline hidden">Wholesale</p>
            <h1 className="font-serif font-light text-3xl sm:text-4xl md:text-5xl text-brand-dark mt-4 leading-[1.1]">
              For retailers that share<br /> our love of <span className="italic">paper</span>.
            </h1>
            <p className="mt-6 text-brand-neutral text-lg leading-relaxed max-w-xl">
              Apply below to view wholesale pricing and place orders. Pre-orders are
              now open for early October delivery.
            </p>
          </div>
          <div className="md:col-span-5 md:pl-6">
            <ul className="space-y-4 text-brand-neutral">
              {[
                { title: "Minimum Order", body: "30 cards (minimum 5 per design)" },
                { title: "Opening Order", body: "$150 minimum" },
                { title: "Payment Terms", body: "50% deposit required for pre-orders and bespoke collections" },
                { title: "Delivery", body: "Available throughout The Bahamas" },
                { title: "Exclusive Collections", body: "Bespoke collections available for select retail partners." },
              ].map((t) => (
                <li key={t.title} className="flex items-start gap-3">
                  <CheckCircle2 strokeWidth={1.5} className="w-5 h-5 text-brand-accent shrink-0 mt-0.5" />
                  <div>
                    <p className="text-brand-dark font-medium">{t.title}</p>
                    <p className="text-sm mt-0.5">{t.body}</p>
                  </div>
                </li>
              ))}
            </ul>
          </div>
        </div>
      </section>

      {approved ? (
        <section className="max-w-[1440px] mx-auto px-6 md:px-12 pb-24">
          <p className="overline hidden">Wholesale catalogue</p>
          <h2 className="font-serif text-3xl md:text-4xl text-brand-dark mt-3">Approved pricing</h2>
          <p className="text-brand-neutral mt-2">Signed in as {user.business_name || user.name}. Wholesale prices now show sitewide.</p>
          <div className="mt-10 border-t border-brand-border">
            <div className="grid grid-cols-12 py-4 border-b border-brand-border text-xs uppercase tracking-widest text-brand-neutral">
              <div className="col-span-6">Product</div>
              <div className="col-span-2">Occasion</div>
              <div className="col-span-2">MSRP</div>
              <div className="col-span-2 text-right">Wholesale</div>
            </div>
            {products.map((p) => (
              <div key={p.slug} data-testid={`wholesale-row-${p.slug}`} className="grid grid-cols-12 py-5 border-b border-brand-border items-center">
                <div className="col-span-6 flex items-center gap-4">
                  <div className="w-14 h-14 overflow-hidden bg-brand-surface">
                    <img src={p.images?.[0]} alt="" className="w-full h-full object-cover" />
                  </div>
                  <div>
                    <p className="font-serif text-lg text-brand-dark">{p.title}</p>
                    <p className="text-xs text-brand-neutral">{p.sku || p.dimensions}</p>
                  </div>
                </div>
                <div className="col-span-2 text-sm text-brand-neutral">{p.occasion}</div>
                <div className="col-span-2 text-brand-neutral">${p.price?.toFixed(2)}</div>
                <div className="col-span-2 text-right font-medium text-brand-accent">${p.wholesale_price?.toFixed(2)}</div>
              </div>
            ))}
          </div>
        </section>
      ) : pending || submitted ? (
        <section className="max-w-[820px] mx-auto px-6 md:px-12 pb-24 text-center">
          <div className="border border-brand-border bg-brand-surface p-12 paper-texture">
            <p className="overline hidden">Application received</p>
            <h2 className="font-serif text-2xl md:text-3xl text-brand-dark mt-4">
              We'll be in touch within <span className="italic">2–3 business days</span>.
            </h2>
            <p className="mt-4 text-brand-neutral">
              Thank you{user?.name ? `, ${user.name}` : ""}. Your wholesale application is being reviewed. You'll receive an email once your account is approved.
            </p>
          </div>
        </section>
      ) : (
        <section className="max-w-[820px] mx-auto px-6 md:px-12 pb-24">
          <div className="border border-brand-border p-8 md:p-12">
            <p className="overline hidden">Apply for a wholesale account</p>
            <h2 className="font-serif text-3xl text-brand-dark mt-3 mb-8">Business details</h2>
            {user ? (
              <p className="text-brand-neutral">
                You're already signed in as {user.email}. To apply for wholesale, please <Link to="/login" className="text-brand-accent">sign in</Link> with a business email.
              </p>
            ) : (
              <form onSubmit={onSubmit} className="grid md:grid-cols-2 gap-x-8 gap-y-6">
                {[
                  ["business_name", "Business name *", "text", true],
                  ["name", "Your name *", "text", true],
                  ["email", "Business email *", "email", true],
                ].map(([k, label, type, req]) => (
                  <label key={k} className="block">
                    <span className="sr-only">{label}</span>
                    <input
                      data-testid={`wholesale-${k}`}
                      type={type}
                      required={req}
                      placeholder={label}
                      value={form[k]}
                      onChange={(e) => setForm({ ...form, [k]: e.target.value })}
                      className="flushed-input"
                    />
                  </label>
                ))}

                {/* Password with show/hide toggle */}
                <label className="block relative">
                  <span className="sr-only">Choose a password</span>
                  <input
                    data-testid="wholesale-password"
                    type={showPw ? "text" : "password"}
                    required
                    placeholder="Choose a password *"
                    value={form.password}
                    onChange={(e) => setForm({ ...form, password: e.target.value })}
                    className="flushed-input pr-9"
                    autoComplete="new-password"
                  />
                  <button
                    type="button"
                    data-testid="wholesale-password-toggle"
                    onClick={() => setShowPw(!showPw)}
                    aria-label={showPw ? "Hide password" : "Show password"}
                    className="absolute right-0 bottom-2 text-brand-neutral hover:text-brand-accent transition-colors"
                  >
                    {showPw ? <EyeOff strokeWidth={1.5} className="w-4 h-4" /> : <Eye strokeWidth={1.5} className="w-4 h-4" />}
                  </button>
                </label>

                {[
                  ["phone", "Phone number", "tel", false],
                  ["business_type", "Business type (boutique, hotel…)", "text", false],
                  ["island", "Island (Bahamas only)", "text", false],
                ].map(([k, label, type, req]) => (
                  <label key={k} className="block">
                    <span className="sr-only">{label}</span>
                    <input
                      data-testid={`wholesale-${k}`}
                      type={type}
                      required={req}
                      placeholder={label}
                      value={form[k]}
                      onChange={(e) => setForm({ ...form, [k]: e.target.value })}
                      className="flushed-input"
                    />
                  </label>
                ))}
                <label className="block md:col-span-2">
                  <span className="sr-only">Position / Role *</span>
                  <select
                    data-testid="wholesale-position"
                    required
                    value={form.position}
                    onChange={(e) => setForm({ ...form, position: e.target.value })}
                    className="flushed-input"
                  >
                    <option value="" disabled>Position / role *</option>
                    <option>Owner</option>
                    <option>Buyer</option>
                    <option>Manager</option>
                    <option>Purchasing Manager</option>
                    <option>Gift Shop Manager</option>
                    <option>Other</option>
                  </select>
                </label>
                <div className="md:col-span-2 mt-8">
                  <button data-testid="wholesale-submit-btn" disabled={busy} className="btn-primary">
                    {busy ? "Submitting…" : "Submit Application"}
                  </button>
                  <p className="text-xs text-brand-neutral mt-4">
                    Already applied? <Link to="/login" className="text-brand-accent">Sign in</Link>.
                  </p>
                </div>
              </form>
            )}
          </div>
        </section>
      )}
    </div>
  );
}
